import type { MaybeRefOrGetter, Ref } from 'vue';
import type { ResolvedMonospaceServiceAccount } from '#layers/data-model/public';
import type { WorkspaceRuntimeHandle } from '#layers/runtime/public';
import { difference } from 'lodash-es';
import { computed, ref, toValue } from 'vue';
import { connectMemberRoles, disconnectMemberRoles } from '#layers/access/utils/api/member-roles';

interface ServiceAccountWorkspaceRoles {
	selectedRoleIds: Ref<string[]>;
	hasRoleChanges: Readonly<Ref<boolean>>;
	captureRoleChanges: (options: { assignableRoleIds: string[] | '*' }) => ServiceAccountRoleChanges;
	saveRoleChanges: (options: { changes: ServiceAccountRoleChanges; serviceAccountId: string }) => Promise<void>;
}

interface ServiceAccountRoleChanges {
	added: string[];
	removed: string[];
}

/**
 * Reads and edits the roles a service account holds in the active workspace `handle` was
 * captured from. Pass `null` where no workspace is active (organization settings) or the
 * account does not exist yet; the returned controls are then inert.
 */
export function useServiceAccountWorkspaceRoles(
	serviceAccount: MaybeRefOrGetter<ResolvedMonospaceServiceAccount | null>,
	handle: WorkspaceRuntimeHandle | null,
): ServiceAccountWorkspaceRoles {
	const serviceAccountId = toValue(serviceAccount)?.id;

	if (!serviceAccountId || !handle) {
		return {
			selectedRoleIds: ref([]),
			hasRoleChanges: computed(() => false),
			captureRoleChanges: () => ({ added: [], removed: [] }),
			async saveRoleChanges() {},
		};
	}

	const { fetch, store } = handle;

	const { data: assignedRoles } = store.MonospaceRole.query((q) =>
		q.many({
			filter: { users: { _some: { userId: serviceAccountId } } },
			// Relational filters can't be resolved from the local cache, so fetch fresh.
			fetchPolicy: 'no-cache',
		}),
	);

	const originalRoleIds = computed(() => (assignedRoles.value ?? []).map((role) => role.id));

	const addedRoleIds = ref<Set<string>>(new Set());
	const removedRoleIds = ref<Set<string>>(new Set());

	const selectedRoleIds = computed<string[]>({
		get() {
			const effective = new Set(originalRoleIds.value);
			for (const id of removedRoleIds.value) effective.delete(id);
			for (const id of addedRoleIds.value) effective.add(id);
			return [...effective];
		},
		set(next) {
			addedRoleIds.value = new Set(difference(next, originalRoleIds.value));
			removedRoleIds.value = new Set(difference(originalRoleIds.value, next));
		},
	});

	const hasRoleChanges = computed(() => addedRoleIds.value.size > 0 || removedRoleIds.value.size > 0);

	function captureRoleChanges({ assignableRoleIds }: { assignableRoleIds: string[] | '*' }): ServiceAccountRoleChanges {
		function canAssign(roleId: string) {
			return assignableRoleIds === '*' || assignableRoleIds.includes(roleId);
		}

		// Only touch roles the user may assign; the backend enforces role:assign:<id> and rejects a
		// detach of a role the user never had rights to.
		return {
			added: [...addedRoleIds.value].filter((id) => canAssign(id)),
			removed: [...removedRoleIds.value].filter((id) => canAssign(id)),
		};
	}

	async function saveRoleChanges({ changes, serviceAccountId }: { changes: ServiceAccountRoleChanges; serviceAccountId: string }) {
		const { added, removed } = changes;

		await connectMemberRoles({ fetch, memberId: serviceAccountId, roleIds: added });
		await disconnectMemberRoles({ fetch, memberId: serviceAccountId, roleIds: removed });

		for (const roleId of added) {
			store.MonospaceUserRole.writeItem({ userId: serviceAccountId, roleId });
		}

		for (const roleId of removed) {
			store.MonospaceUserRole.clearItem(`${serviceAccountId}__${roleId}`);
		}
	}

	return {
		selectedRoleIds,
		hasRoleChanges,
		captureRoleChanges,
		saveRoleChanges,
	};
}
